import { useState } from "react";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
} from "../financialProjection/cashFlowPlanning";
import { regenerateAmpliDisbursement } from "./disbursementApi";
import { money } from "./format";
import {
  normalizeDisbursementCoverage,
  coverageServicesLabel,
  hasOriginCoverage,
} from "./coverage";
import { ORIGIN_FOLLOWS, ORIGIN_FOLLOWS_OBJECT } from "./OriginSelector";

const STATE_HINT = Object.freeze({
  ready: "As regras cadastradas já estão refletidas na Projeção Financeira.",
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  inconsistent: null, // vem do motivo
  unavailable: "Esta obra ainda não tem previsto canônico para gerar a curva.",
});

// `coverage.percentage` é uma FRAÇÃO (0..1), não basis points. O nome é
// `fractionLabel` de propósito: `format.js#percentLabel` opera em bps (o editor de
// regra grava/lê bps) e os dois já foram confundidos numa revisão.
const fractionLabel = (value) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

/**
 * Folha da linha de cobertura: `money()` lê `useProjectContext` por dentro, e o bloco
 * inteiro é renderizado atrás de `coverage ? ... : null` — chamado ali direto, a
 * contagem de hooks do cabeçalho mudaria no instante em que a cobertura chegasse.
 *
 * Com a divisão da origem (D14) a cobertura é o valor COM distribuição no tempo sobre o
 * valor de referência da obra, e os três pedaços ficam lado a lado: o que segue a origem, o
 * que tem regra própria e o que está sem distribuição. Um payload gravado antes de a origem
 * existir (sem a referência) cai na leitura antiga, a de regras.
 */
const CoverageHeadline = ({ coverage, followsText }) =>
  hasOriginCoverage(coverage) ? (
    <div className="ampli-disbursement-coverage__headline">
      <div>
        <span>Cobertura</span>
        <strong>{fractionLabel(coverage.distributedPercentage)}</strong>
      </div>
      <div data-testid="coverage-follows">
        <span>{followsText}</span>
        <strong>{money(coverage.followsBaseCents)}</strong>
      </div>
      <div data-testid="coverage-ruled">
        <span>Regra própria</span>
        <strong>{money(coverage.ruledCents)}</strong>
      </div>
      <div data-testid="coverage-undistributed">
        <span>Sem distribuição</span>
        <strong>{money(coverage.undistributedCents)}</strong>
      </div>
      <div>
        <span>Valor de referência</span>
        <strong>{money(coverage.referenceCents)}</strong>
      </div>
    </div>
  ) : (
    <div className="ampli-disbursement-coverage__headline">
      <div>
        <span>Cobertura</span>
        <strong>{fractionLabel(coverage.percentage)}</strong>
      </div>
      <div>
        <span>Previsto com regra</span>
        <strong>{money(coverage.ruledCents)}</strong>
      </div>
      <div>
        <span>Previsto total</span>
        <strong>{money(coverage.scheduledCents)}</strong>
      </div>
    </div>
  );

const generatedAtLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `gerado em ${date.toLocaleDateString(
        "pt-BR",
      )} às ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
};

const DisbursementHeader = ({
  project,
  readOnly = false,
  onRegenerated,
  moduleState,
  origin = null,
}) => {
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(false);
  const displayProject =
    moduleState === undefined
      ? project
      : {
          ...project,
          ampliDisbursement: moduleState
            ? { ...moduleState, state: moduleState.status }
            : null,
        };
  const curve = ampliCurveState(displayProject);
  const coverage = normalizeDisbursementCoverage(
    displayProject?.ampliDisbursement?.coverage,
  );
  const originCoverage = hasOriginCoverage(coverage);
  const followsText = origin?.id ? ORIGIN_FOLLOWS[origin.id] : "Segue a origem";
  const followsObject = origin?.id ? ORIGIN_FOLLOWS_OBJECT[origin.id] : "a origem";
  const widthOf = (cents) =>
    originCoverage && coverage.referenceCents > 0
      ? Math.min(100, Math.max(0, (cents / coverage.referenceCents) * 100))
      : 0;
  const ruledWidth = originCoverage
    ? widthOf(coverage.ruledCents)
    : coverage?.percentage != null
      ? Math.min(100, Math.max(0, coverage.percentage * 100))
      : 0;
  const railLabel = originCoverage
    ? `${fractionLabel(coverage.distributedPercentage)} do valor de referência com distribuição no tempo`
    : `${fractionLabel(coverage?.percentage)} do previsto com regra cadastrada`;
  const generatedAt = generatedAtLabel(curve.generatedAt);
  const hint = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_HINT[curve.status];

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(false);
    try {
      const result = await regenerateAmpliDisbursement(project?._id);
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setError(true);
        return;
      }
      await onRegenerated?.(result);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <section
      className="ampli-disbursement-header"
      aria-label="Situação do desembolso Ampli"
    >
      <hgroup className="dashboard-title">
        <h2>Desembolso</h2>
        <p>
          Cadastre a forma de pagar por família de insumo, insumo ou nível da
          EAP. A plataforma redistribui o previsto no tempo e corrige a curva
          sozinha quando o previsto muda ou o item é contratado.
        </p>
      </hgroup>

      <div className="ampli-disbursement-header__state">
        <span
          className={`ampli-disbursement-badge ampli-disbursement-badge--${curve.status}`}
        >
          {ampliCurveStateLabel(curve.status)}
        </span>
        {generatedAt ? (
          <span className="ampli-disbursement-header__timestamp">
            {generatedAt}
          </span>
        ) : null}
        <p className="ampli-disbursement-header__hint">{hint}</p>
      </div>

      {coverage ? (
        <div className="ampli-disbursement-coverage">
          <CoverageHeadline coverage={coverage} followsText={followsText} />
          {(originCoverage ? coverage.distributedPercentage : coverage.percentage) !== null ? (
            <div
              className="ampli-disbursement-coverage__rail"
              role="img"
              aria-label={railLabel}
            >
              <span
                className="coverage-segment ruled"
                style={{ width: `${ruledWidth}%` }}
              />
              {originCoverage ? (
                <>
                  <span
                    className="coverage-segment follows-base"
                    style={{ width: `${widthOf(coverage.followsBaseCents)}%` }}
                  />
                  <span
                    className="coverage-segment undistributed"
                    style={{ width: `${widthOf(coverage.undistributedCents)}%` }}
                  />
                </>
              ) : (
                <span
                  className="coverage-segment at-sight"
                  style={{ width: `${100 - ruledWidth}%` }}
                />
              )}
            </div>
          ) : (
            <p>Cobertura percentual indisponível.</p>
          )}
          <p className="ampli-disbursement-coverage__legend">
            {coverageServicesLabel(coverage, { followsLabel: followsObject })}
          </p>
          {coverage.orphanRules?.length > 0 ? (
            <p className="alert alert-info" role="status">
              {`${coverage.orphanRules.length} regra(s) sem previsto abaixo do alvo — nada foi redistribuído por elas.`}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* O resumo só informa; criar regra/grupo e organizar com IA moram no bloco das
          regras já criadas. A única ação daqui é sobre a própria curva. */}
      {!readOnly &&
      (curve.status === "stale" || curve.status === "inconsistent") ? (
        <div className="ampli-disbursement-header__actions">
          <button
            type="button"
            className="button button-outline small"
            disabled={regenerating}
            onClick={regenerate}
          >
            {regenerating ? "Gerando…" : "Gerar de novo"}
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="alert alert-error" role="alert">
          Não foi possível gerar a curva agora. Tente novamente.
        </p>
      ) : null}
    </section>
  );
};

export default DisbursementHeader;
